import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface FieldProps {
  label: ReactNode;
  /** Receives the generated control id and the id of the hint/error text for aria-describedby. */
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
}

/** Label + control row (Linear-style property list): label left, control right. */
export function Field({ label, children, hint, error, className }: FieldProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error ?? hint;
  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-start gap-x-3 gap-y-1',
        className,
      )}
    >
      <label htmlFor={id} className="pt-1.5 text-xs text-text-3">
        {label}
      </label>
      <div className="min-w-0">
        {children({ id, describedBy: note === undefined ? undefined : noteId })}
        {note !== undefined && (
          <p
            id={noteId}
            className={cn('mt-1 text-xs', error === undefined ? 'text-text-3' : 'text-negative')}
          >
            {note}
          </p>
        )}
      </div>
    </div>
  );
}

export function Section({
  title,
  children,
  actions,
}: {
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="border-t border-border-subtle px-4 py-3 first:border-t-0">
      <div className="mb-2 flex h-6 items-center justify-between">
        <h3 className="text-xs font-medium text-text-2">{title}</h3>
        {actions}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
